import { unavailableItemMessages } from '@app/components/Messages';
import { ObjectValues } from '@app/types';
import { Severity, Type } from '@app/types/monolith/cart';
import {
  Alert,
  Box,
  CheckDiscIcon,
  constants,
  Icon,
  InfoDiscIcon,
  Stack,
  Text,
  TextLink,
} from '@redbubble/design-system';
import { useIntl } from '@redbubble/next/intl';
import { UserInfo } from '@redbubble/next/types';
import { useUserInfo } from '@redbubble/next-provider';
import React from 'react';
import styles from './Messages.module.scss';

export const CART_TRAY_MSG_SEVERITY = Severity;
export const CART_TRAY_MSG_TYPE = {
  ...Type,
  Error: 'error',
  ItemAdded: 'item_added',
} as const;

export type CartTrayMessageType = ObjectValues<typeof CART_TRAY_MSG_TYPE>;
export type CartTrayMessageSeverityType = ObjectValues<
  typeof CART_TRAY_MSG_SEVERITY
>;

const SeverityToConstants = {
  [Severity.Error]: constants.ERROR,
  [Severity.Info]: constants.INFORMATION,
  [Severity.Success]: constants.SUCCESS,
  [Severity.Warning]: constants.WARNING,
} as const;

// TODO check with Tora if these colours are acceptable
const IconColorBySeverity: Record<CartTrayMessageSeverityType, string> = {
  [Severity.Success]: 'var(--ds-color-success-600)',
  [Severity.Info]: 'var(--ds-color-information-600)',
  [Severity.Warning]: 'var(--ds-color-warning-600)',
  [Severity.Error]: 'var(--ds-color-error-600)',
};

const IconComponentByName: Record<IconName, Icon> = {
  'check-disc': CheckDiscIcon,
  'info-circle': InfoDiscIcon,
} as const;

type IconName = 'check-disc' | 'info-circle';

export type Message = {
  action_url?: string;
  applied?: boolean;
  discount_key?: string | null;
  discount_product_type_ids?: string[];
  product_name?: string | null;
  severity: CartTrayMessageSeverityType;
  text?: string;
  type: CartTrayMessageType;
  title?: string;
  priority?: Number | null;
  icon?: IconName;
};

export interface MessagesProps {
  messages: Message[];
}

type TextNodeProps = {
  text: string;
  messageType: CartTrayMessageType;
  locale: string;
  actionUrl?: string;
};

const getLocalisedHelpCenterUrl = (
  url: string,
  messageType: CartTrayMessageType,
  locale: string
) => {
  if (messageType === CART_TRAY_MSG_TYPE.NonsaleableProductRemoval) {
    return url.replace(
      '/articles/',
      `/${locale === 'en' ? 'en-us' : locale}/articles/`
    );
  }

  return url;
};

const TextNode: React.FC<TextNodeProps> = ({
  actionUrl,
  text,
  messageType,
  locale,
}) => {
  if (actionUrl) {
    return (
      <TextLink
        href={getLocalisedHelpCenterUrl(actionUrl, messageType, locale)}
      >
        {text}
      </TextLink>
    );
  }

  return <Text>{text}</Text>;
};

const IconNode = ({ icon, severity }) => {
  if (!icon || !IconComponentByName[icon]) {
    return null;
  }

  const Icon = IconComponentByName[icon];
  return <Icon size="medium" color={IconColorBySeverity[severity]} />;
};

const getMessageText = (type, text, intl): string => {
  if (type === CART_TRAY_MSG_TYPE.NonsaleableProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.nonSaleableItem);
  }
  if (type === CART_TRAY_MSG_TYPE.ExpiredProductRemoval) {
    return intl.formatMessage(unavailableItemMessages.expiredItem);
  }
  return text;
};

export const Messages: React.FC<MessagesProps> = ({ messages }) => {
  const intl = useIntl();
  const [{ locale }] = useUserInfo<UserInfo>();

  return (
    <>
      {messages.map(
        (
          {
            type: msgType,
            discount_key: discountKey,
            severity,
            action_url: actionUrl,
            text,
            title,
            icon,
          },
          index
        ) => {
          const alertProps: {
            intent: ObjectValues<typeof constants>;
            label: boolean;
            centered: boolean;
            title?: string;
          } = {
            intent: SeverityToConstants[severity],
            label: false,
            // TODO check with Toria whether we make this consistently centred or if we left-align with an icon e.g. successfully added item to cart
            // See: https://redbubble.slack.com/archives/C04NW1DN820/p1757570426649919
            centered: false, // !icon, or something else??
          };

          if (severity === 'error') {
            if (title) alertProps.title = title;
            alertProps.label = true;
          }

          return (
            <Box
              key={`${discountKey || msgType}-${index}`}
              marginTop={index === 0 ? 'none' : 's'}
              className={styles.container}
            >
              <Alert {...alertProps}>
                <Stack spacing="xs" direction="horizontal">
                  <IconNode icon={icon} severity={severity} />
                  <TextNode
                    actionUrl={actionUrl}
                    text={getMessageText(msgType, text, intl)}
                    messageType={msgType}
                    locale={locale}
                  />
                </Stack>
              </Alert>
            </Box>
          );
        }
      )}
    </>
  );
};
